import {
  actuatorBusIdRange,
  actuatorDriverOptions,
  actuatorOptions,
  actuatorRpmOptions,
  actuatorTravelDegrees,
  addObjectFromFolder,
  cameraOptions,
  componentOptions,
  createEnv,
  microcontrollerOptions,
  planRobotFromFolder,
  powerOptions,
  psramOptions,
  registerFolderDrop,
  stepDownOptions,
  stepDownOutputVolts,
  AddedObject,
  AddedRobot,
  DroppedFolder,
  InterfaceHybrid,
  RobotPlan,
} from "simulator";
import { Simulator } from "gorilla-physics-ui";
import { LineSegments, Matrix4, Mesh, Quaternion, Vector3 } from "three";

import {
  applyConfig,
  configFromPlan,
  readViewRotations,
  readSavedConfig,
  shallowestFile,
  CONFIG_FILE,
} from "./config";
import { cameraViewRotations, showCameraViewOf } from "./camera";
import { ARDUINO_NANO } from "./compile";
import {
  editFirmwareOf,
  readDroppedSketches,
  type DroppedSketch,
} from "./editor";
import { allowFixing, disarmFixing, setFixingAllowed } from "./fix";
import { connectNetworkOf } from "./network";
import { allowNudging, disarmNudging, setNudgingAllowed } from "./nudge";
import { placeObject } from "./place";
import { useJoysticksOf } from "./joysticks";
import { pressSwitchesOf } from "./switches";
import { addWarnings, clearWarnings, showWarnings } from "./warnings";

const DROP_PROMPT =
  "Drop a robot folder (URDF + meshes), or an object (a mesh with no URDF), here";

function showStatus(message: string) {
  const status = document.getElementById("drop-status");
  if (status) {
    status.textContent = message;
  }
}

// `Simulator.realtimeRatio` is recomputed once per smoothing window inside the
// run loop, so it is polled rather than pushed -- twice a second, which is slow
// enough to read and fast enough to show a robot dragging the loop down.
//
// The rate says a scene is behind; the breakdown under it says what is holding
// it up. That is a click away rather than always on: it is the answer to a
// question most sessions never ask, and it costs a line per phase in the corner
// of the view. `hybrid.profile_take` accumulates whether or not anyone is
// looking (see `hybrid::profile` in the engine), so opening it mid-run needs
// nothing switched on first.
function showRealtimeRate(simulator: Simulator, hybrid: InterfaceHybrid) {
  const rate = document.getElementById("realtime-rate");
  const value = document.getElementById("realtime-value");
  const profile = document.getElementById("realtime-profile");
  if (!rate || !value || !profile) {
    return;
  }

  const labels = hybrid.profile_labels().split(",");
  let lastPoll = performance.now();

  rate.addEventListener("click", () => {
    profile.hidden = !profile.hidden;
    // Whatever piled up while it was closed describes a window the user did not
    // ask about, and would land on screen as the first reading.
    hybrid.profile_take();
    lastPoll = performance.now();
  });

  setInterval(() => {
    value.textContent = `realtime rate: ${simulator.realtimeRatio.toFixed(2)}`;

    // Reading resets the accumulation, so this has to run every tick whether or
    // not the panel is up -- otherwise the first reading after it opens covers
    // however long it was shut.
    const taken = hybrid.profile_take();
    const now = performance.now();
    const wall = now - lastPoll;
    lastPoll = now;
    if (profile.hidden) {
      return;
    }

    const steps = taken[0];
    const phases = Array.from(taken.slice(1));
    if (steps === 0 || wall <= 0) {
      profile.textContent = "not stepping";
      return;
    }

    // Two numbers per phase, because they answer different questions: the share
    // of the wall clock is what identifies the bottleneck, and the cost per
    // step is what a change to that phase moves. Sorted, since the first line is
    // the answer.
    const order = phases
      .map((ms, i) => ({ name: labels[i] ?? `phase ${i}`, ms }))
      .sort((a, b) => b.ms - a.ms);
    const total = phases.reduce((sum, ms) => sum + ms, 0);
    const lines = order.map(
      ({ name, ms }) =>
        `${name.padEnd(12)}${((100 * ms) / wall).toFixed(0).padStart(3)}% ` +
        `${((1000 * ms) / steps).toFixed(0).padStart(5)} us`,
    );
    profile.textContent = [
      `${(steps / (wall / 1000)).toFixed(0)} steps/s, ` +
        `${((100 * total) / wall).toFixed(0)}% of wall in step`,
      ...lines,
    ].join("\n");
  }, 500);
}

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Create the three.js objects for one articulated. Simulator.addHybrid does this
// for a whole scene at startup; a robot added later needs the same treatment
// for its own index, after which the run loop poses it like everything else.
function addArticulatedVisuals(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
  i: number,
) {
  const n_bodies = hybrid.n_bodies_articulated(i);
  for (let j = 0; j < n_bodies; j++) {
    const frame = hybrid.frame_articulated_body(i, j);
    const n_visuals = hybrid.n_visuals_articulated_body(i, j);
    for (let k = 0; k < n_visuals; k++) {
      const iso = hybrid.iso_visual_to_body(i, j, k);
      const offset = new Matrix4().compose(
        new Vector3(iso[4], iso[5], iso[6]),
        new Quaternion(iso[0], iso[1], iso[2], iso[3]),
        new Vector3(1, 1, 1),
      );
      const name = `${frame}-${k}`;
      const visual_type = hybrid.visual_type(i, j, k);

      if (visual_type == 2) {
        simulator.addRigidMesh(
          name,
          hybrid.visual_color(i, j, k),
          hybrid.visual_mesh_vertices(i, j, k),
          hybrid.visual_mesh_faces(i, j, k),
          offset,
          // Empty unless the mesh was an OBJ naming materials, in which case
          // the .mtl's colours are drawn per group of faces.
          hybrid.visual_mesh_groups(i, j, k),
          hybrid.visual_mesh_group_colors(i, j, k),
        );
      } else if (hybrid.visual_show(i)) {
        // Spheres, cuboids and points are the collision proxies, drawn only when
        // the robot has no meshes of its own.
        if (visual_type == 0) {
          simulator.addSphere(
            name,
            0xff0000,
            hybrid.visual_sphere_r(i, j, k),
            offset,
          );
        } else if (visual_type == 1) {
          const wdh = hybrid.visual_cuboid_wdh(i, j, k);
          simulator.addCuboid(name, 0xff0000, wdh[0], wdh[1], wdh[2], offset);
        } else if (visual_type == 3) {
          simulator.addSphere(name, 0xff0000, 0.002, offset);
        }
      }
    }
  }
}

// Take the three.js objects `addArticulatedVisuals` made for one articulated
// back out of the scene. The names are rebuilt the same way they were made, so
// this has to run while the articulated is still in the `Hybrid` -- it is what
// `frame_articulated_body` is asked for.
//
// Meshes are disposed rather than merely unparented: a robot is tens of
// megabytes of vertex buffers on the GPU, and dropping folders one after
// another would otherwise leak every one of them.
function removeArticulatedVisuals(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
  i: number,
) {
  const n_bodies = hybrid.n_bodies_articulated(i);
  for (let j = 0; j < n_bodies; j++) {
    const frame = hybrid.frame_articulated_body(i, j);
    const n_visuals = hybrid.n_visuals_articulated_body(i, j);
    for (let k = 0; k < n_visuals; k++) {
      const name = `${frame}-${k}`;
      // A cuboid also drew its outline, under the same name in the other map.
      const mesh = simulator.meshes.get(name);
      if (mesh) {
        simulator.graphics.scene.remove(mesh);
        dispose(mesh);
        simulator.meshes.delete(name);
      }
      const edges = simulator.edgesMeshes.get(name);
      if (edges) {
        simulator.graphics.scene.remove(edges);
        dispose(edges);
        simulator.edgesMeshes.delete(name);
      }
    }
  }
}

// The GPU-side halves of one object. A mesh drawn from a `.mtl` carries an
// array of materials, one per group of faces, rather than a single one.
function dispose(object: Mesh | LineSegments) {
  object.geometry.dispose();
  for (const material of Array.isArray(object.material)
    ? object.material
    : [object.material]) {
    material.dispose();
  }
}

// Leave the robot just added alone in the scene, in both halves: the three.js
// objects here, and the `Articulated` with the emulated board driving it in
// wasm. A drop replaces what is on screen rather than joining it.
//
// The order is what makes it work. Two robots read from the same URDF name
// their bodies the same, so `addArticulatedVisuals` on the new one would take
// the old one's place in `simulator.meshes` and leave the old meshes in the
// scene with nothing posing them -- a robot dropped exactly on top of the one
// already there, apparently doing nothing, which is what a second drop used to
// look like. So the old visuals go first, while their articulated is still in
// the `Hybrid` to be named by; `removeOtherRobots` then renumbers `added` to
// index 0, and only then are the new visuals made.
function keepOnlyRobot(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
  added: AddedRobot,
  objects: AddedObject[],
) {
  // The objects placed in the scene are not the robot's to clear away: an
  // object drop joins what is there rather than replacing it. They are
  // `Articulated`s like the robots -- a free body is a one-body articulated --
  // so nothing in the scene tells them apart, and the handles here are what
  // says which is which.
  const keep = objects.map((object) => object.index);
  for (let i = 0; i < hybrid.n_articulated(); i++) {
    if (i !== added.index && !keep.includes(i)) {
      removeArticulatedVisuals(simulator, hybrid, i);
    }
  }
  // Everything that survives is renumbered: the robot to 0 and the objects
  // after it, in the order they were given. Their meshes are named after their
  // bodies' frames rather than their indices, so nothing on screen moves --
  // only the handles have to be told.
  const moved = added.removeOtherRobots(hybrid, new Uint32Array(keep));
  objects.forEach((object, k) => object.reindex(moved[k]));
}

// The control goes inside its label: they are built here rather than in
// index.html, so there is no id for a `for` attribute to point at.
function labelledField(text: string, control: HTMLElement): HTMLLabelElement {
  const label = document.createElement("label");
  label.append(text, control);
  return label;
}

// A select that starts on a placeholder, so a choice is always the user's and
// never the browser's first option.
function choiceSelect(
  placeholder: string,
  choices: string[],
): HTMLSelectElement {
  const select = document.createElement("select");
  const prompt = new Option(placeholder, "");
  select.append(prompt, ...choices.map((choice) => new Option(choice, choice)));
  return select;
}

// The actuator select's answer for a joint left with nothing on it: a joint
// that swings with whatever it is attached to. Not an actuator name Rust knows,
// so it is kept off `setActuator` and written with `setFree` instead.
const FREE_JOINT = "free";

// The speed a gearmotor's select starts on, which is the one Rust fits it at
// (`N20_DEFAULT_RPM`) -- so a panel left alone and a plan never told agree.
const DEFAULT_MOTOR_RPM = "300";

// A driver select's answer for a driver that is not on the board yet, followed
// by its kind: `new:DRV8833`. Every other answer is `driver:channel`, both
// indices into the plan.
const NEW_DRIVER = "new:";

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

// Put a plan back to the configuration in `config`, after a panel opened on it
// was cancelled. `configureRobot` writes the hardware step into the plan as it
// is left, so a user who presses "Next" and then "Cancel" has already moved the
// plan the robot in the scene was built from.
//
// `applyConfig` is written for a freshly read plan: it *adds* the components the
// file lists rather than matching them against what is there, and it leaves a
// joint the file says nothing about exactly as it found it. So the components
// and the actuators come off first, and what the file then puts back is
// precisely what it describes -- which, this being the file the running robot
// was built from, is where the plan started.
function restoreConfig(plan: RobotPlan, config: string) {
  for (let c = plan.componentCount - 1; c >= 0; c--) {
    plan.removeComponent(c);
  }
  for (let j = 0; j < plan.jointCount; j++) {
    plan.clearActuator(j);
  }
  // These are notes about the configuration the robot is already running under,
  // so they are not news to anyone: the console is far enough.
  for (const note of applyConfig(plan, config)) {
    console.warn(`restoring the configuration of ${plan.name}: ${note}`);
  }
}

// A line of muted text standing in for a list that has no rows at all.
function emptyNote(text: string): HTMLDivElement {
  const note = document.createElement("div");
  note.className = "joint-limit";
  note.style.padding = "10px 0";
  note.textContent = text;
  return note;
}

// What the list below is: the panel holds two of them in one scrolling column,
// so each says which.
function sectionLabel(text: string): HTMLDivElement {
  const label = document.createElement("div");
  label.className = "section-label";
  label.textContent = text;
  return label;
}

// What the panel has to read back off one joint once the user commits.
interface JointRow {
  actuator: HTMLSelectElement;
  angle: HTMLInputElement;
  // Hidden, and not read back, for an actuator with no horn to read -- a
  // gearmotor turns as long as it is driven, so there is no reading to ask for.
  angleField: HTMLElement;
  // The speed a gearmotor was bought at, which is its gearbox -- hidden, and not
  // read back, for an actuator that comes in one version.
  rpm: HTMLSelectElement;
  rpmField: HTMLElement;
  // The driver a motor is wired through, and the channel of it -- hidden for
  // an actuator that needs none. Unlike the rest of the row this is written
  // into the plan the moment it changes rather than when the step is left:
  // which channels are free is shared by every motor's select, and the plan is
  // what keeps a driver to as many motors as it has channels.
  driver: HTMLSelectElement;
  driverField: HTMLElement;
  // The address a bus servo answers to, and the field it sits in -- hidden, and
  // not read back, for an actuator commanded by a pulse instead. A pulse servo
  // is addressed by the pin it listens to, which the wiring step asks for.
  busId: HTMLInputElement;
  busIdField: HTMLElement;
}

// Ask what hardware drives the dropped robot and how it is wired, writing the
// answers into the plan as each step is left. Resolves true once the user
// commits, false if they discard it -- either way nothing has been built yet,
// that is the caller's next move.
//
// `notes` is what a saved configuration could not account for. The panel only
// opens at all when there is something left to answer, so the notes say why the
// user is looking at it; the controls below start on whatever that file did
// manage to put into the plan.
function configureRobot(plan: RobotPlan, notes: string[]): Promise<boolean> {
  const panel = element<HTMLDivElement>("robot-config");
  const hardware = element<HTMLDivElement>("robot-hardware");
  const jointList = element<HTMLDivElement>("robot-joints");
  const cameraList = element<HTMLDivElement>("robot-cameras");
  const switchList = element<HTMLDivElement>("robot-switches");
  const componentActions = element<HTMLDivElement>("robot-component-actions");
  const wiring = element<HTMLDivElement>("robot-wiring");
  const wiringBoard = element<HTMLDivElement>("robot-wiring-board");
  const wiringPower = element<HTMLDivElement>("robot-wiring-power");
  const wiringJoints = element<HTMLDivElement>("robot-wiring-joints");
  const wiringSwitches = element<HTMLDivElement>("robot-wiring-switches");
  const note = element<HTMLDivElement>("robot-config-note");
  const problem = element<HTMLDivElement>("robot-config-problem");
  const caution = element<HTMLDivElement>("robot-config-caution");
  const next = element<HTMLButtonElement>("robot-config-next");
  const back = element<HTMLButtonElement>("robot-config-back");
  const add = element<HTMLButtonElement>("robot-config-add");
  const cancel = element<HTMLButtonElement>("robot-config-cancel");

  element("robot-config-title").textContent = `Configure ${plan.name}`;

  // Only what the file left unanswered, and only the first few: the console has
  // the lot, and the panel is 300px wide.
  note.textContent = notes.slice(0, 3).join(" · ");
  note.hidden = notes.length === 0;

  const board = choiceSelect(
    "Select a microcontroller…",
    microcontrollerOptions(),
  );
  // The PSRAM setting sketches are compiled with, for a board that has one.
  // No placeholder: the board's default is a working answer, and it is the one
  // the plan starts on. Its options follow the board select rather than the
  // plan, which is not told the board until the step is left.
  const psram = document.createElement("select");
  const psramField = labelledField("PSRAM", psram);
  const setPsramOptions = () => {
    const options = psramOptions(board.value);
    const kept = psram.value;
    psram.replaceChildren(...options.map((name) => new Option(name, name)));
    if (options.includes(kept)) {
      psram.value = kept;
    }
    psramField.hidden = options.length === 0;
  };
  element("robot-microcontroller").replaceChildren(
    labelledField("Microcontroller", board),
    psramField,
  );

  // What the robot runs on, as a section of its own heading the list. No
  // placeholder for the reason PSRAM has none: the plan starts on the first
  // source, which is a working answer.
  const power = document.createElement("select");
  power.replaceChildren(
    ...powerOptions().map((name) => new Option(name, name)),
  );
  power.value = plan.power;
  // And what takes it down to the board's rail, each option labelled with
  // what it puts out. No placeholder, for the same reason.
  const stepDown = document.createElement("select");
  stepDown.replaceChildren(
    ...stepDownOptions().map((name) => {
      const volts = stepDownOutputVolts(name);
      return new Option(
        volts === undefined ? name : `${name} (${volts}V out)`,
        name,
      );
    }),
  );
  stepDown.value = plan.stepDown;
  const stepDownField = labelledField("Step-down converter", stepDown);
  stepDownField.className = "joint-field";
  const powerRow = document.createElement("div");
  powerRow.className = "joint";
  powerRow.append(labelledField("Power source", power), stepDownField);
  element("robot-power").replaceChildren(sectionLabel("Power"), powerRow);
  // A saved configuration has already been written into the plan, so the board
  // it named is the one on show. Unset is the placeholder, which is what an
  // unconfigured plan gives back.
  board.value = plan.microcontroller ?? "";
  setPsramOptions();
  if (plan.psram !== undefined) {
    psram.value = plan.psram;
  }
  board.addEventListener("change", setPsramOptions);

  const rows: JointRow[] = [];
  const actuators = actuatorOptions();
  // One select per camera link the URDF draws. They are not wired to anything,
  // so unlike a joint's actuator a camera is a single answer and nothing
  // follows from it -- but it is still an answer the panel exists to collect.
  const cameraRows: HTMLSelectElement[] = [];

  // Every joint needs an answer -- an actuator, or left free -- and every
  // actuator an angle it can reach, and every camera link a sensor, before
  // there is anything to wire up.
  const check = () => {
    next.disabled = !(
      board.value !== "" &&
      rows.every(
        (row) =>
          row.actuator.value !== "" &&
          (row.angleField.hidden || row.angle.checkValidity()) &&
          (row.busIdField.hidden || row.busId.checkValidity()) &&
          (row.driverField.hidden || row.driver.value !== ""),
      ) &&
      cameraRows.every((sensor) => sensor.value !== "")
    );
  };
  board.addEventListener("change", check);

  // Every motor's driver select, filled from the plan: the placeholder, then
  // each channel of each driver on the board that is free or already this
  // joint's, then a new driver of each kind the actuator can be wired through.
  // A driver with no channel left to offer is listed but cannot be picked, so
  // it is plain why it is not an option. Re-run whenever any of them changes,
  // since a channel one motor takes is one every other motor loses -- and a
  // driver left with no motor goes, which renumbers the ones after it.
  const refreshDrivers = () => {
    rows.forEach((row, j) => {
      const kinds = actuatorDriverOptions(row.actuator.value);
      row.driverField.hidden = kinds.length === 0;
      if (row.driverField.hidden) {
        row.driver.replaceChildren();
        return;
      }
      const options = [new Option("Select a driver…", "")];
      for (let d = 0; d < plan.driverCount; d++) {
        if (!kinds.includes(plan.driverKind(d) ?? "")) {
          continue;
        }
        const name = plan.driverName(d) ?? `driver ${d}`;
        const channels = plan.driverChannelCount(d);
        let offered = 0;
        for (let c = 0; c < channels; c++) {
          const holder = plan.driverChannelJoint(d, c);
          if (holder !== undefined && holder !== j) {
            continue;
          }
          const channel = plan.driverChannelName(d, c) ?? String(c);
          options.push(new Option(`${name}, channel ${channel}`, `${d}:${c}`));
          offered += 1;
        }
        if (offered === 0) {
          const full = new Option(
            `${name} (all ${channels} channels in use)`,
            "",
          );
          full.disabled = true;
          options.push(full);
        }
      }
      for (const kind of kinds) {
        options.push(new Option(`New ${kind}`, `${NEW_DRIVER}${kind}`));
      }
      row.driver.replaceChildren(...options);
      const d = plan.motorDriver(j);
      const c = plan.motorChannel(j);
      row.driver.value =
        d === undefined || c === undefined ? "" : `${d}:${c}`;
    });
  };

  jointList.replaceChildren(sectionLabel("Joints"));
  for (let j = 0; j < plan.jointCount; j++) {
    const row = document.createElement("div");
    row.className = "joint";

    const name = document.createElement("div");
    name.className = "joint-name";
    name.textContent = plan.jointName(j) ?? `joint ${j}`;

    const actuator = choiceSelect("Select an actuator…", actuators);
    // Straight under the placeholder rather than among the actuators: it is
    // the answer that fits nothing.
    actuator.add(new Option("None (free joint)", FREE_JOINT), 1);
    const actuatorField = labelledField("Actuator", actuator);
    actuatorField.className = "joint-field";

    // The reading the horn shows in the robot's zero pose, in the servo's own
    // degrees -- not the joint's. The robot is built in the pose its URDF
    // draws, and everything the sketch later writes is measured off this.
    // Only worth asking once an actuator has been chosen: the range is that
    // actuator's whole travel, 0-180 deg on a servo, whatever the joint's own
    // limit turns out to be.
    const angle = document.createElement("input");
    angle.type = "number";
    // Any angle in range, rather than steps counted off one end -- the useful
    // positions of a horn are not a grid.
    angle.step = "any";
    angle.required = true;
    // Beside the box, the range it will take: the chosen actuator's travel and
    // nothing else. It is there to say what may be typed, so it has to be the
    // servo's own limits -- the joint angles this ends up reaching are a
    // consequence of the answer rather than a bound on it, and showing those
    // here read as a restriction on the box that was not one.
    const range = document.createElement("span");
    range.className = "joint-limit";

    // The horn's travel, which is what the reading has to be inside. Rounded
    // outwards to a tenth of a degree so a rounded entry is never rejected as
    // out of range; Rust clamps to the exact travel when the actuator is fitted.
    const setTravel = () => {
      const travel = actuatorTravelDegrees(actuator.value);
      if (travel.length < 2) {
        angle.removeAttribute("min");
        angle.removeAttribute("max");
        range.textContent = "";
        return;
      }
      const [low, high] = travel;
      angle.min = String(Math.floor(low * 10) / 10);
      angle.max = String(Math.ceil(high * 10) / 10);
      // Off the attributes rather than off `travel`, so what is shown is
      // exactly what the box accepts.
      range.textContent = `${angle.min}° to ${angle.max}°`;
      // Mid-travel until the user says otherwise: it is the reading a horn is
      // most often left at, and it puts as much of the servo's travel on one
      // side of the robot's current pose as on the other.
      if (angle.value === "") {
        angle.value = ((low + high) / 2).toFixed(1);
      }
      angle.value = String(
        clamp(angle.valueAsNumber, Number(angle.min), Number(angle.max)),
      );
    };

    const angleRow = document.createElement("div");
    angleRow.className = "joint-angle";
    angleRow.append(angle, range);
    const angleField = labelledField("Zero-pose servo angle (deg)", angleRow);
    angleField.className = "joint-field";

    // The address a bus servo answers to. Only asked about for an actuator that
    // is on a bus -- Rust says which by handing back a range at all -- because a
    // pulse servo is addressed by the pin it listens to, which is the wiring
    // step's question. It is what a sketch writes in `WritePosEx(id, ...)`, so
    // it is the number that has to match the servos actually bolted to the
    // robot rather than anything the simulation decides.
    const busId = document.createElement("input");
    busId.type = "number";
    busId.step = "1";
    busId.required = true;
    const busRange = document.createElement("span");
    busRange.className = "joint-limit";
    const busIdRow = document.createElement("div");
    busIdRow.className = "joint-angle";
    busIdRow.append(busId, busRange);
    const busIdField = labelledField("Bus address", busIdRow);
    busIdField.className = "joint-field";

    // Shown, and defaulted, from what the chosen actuator turns out to be. The
    // default is the lowest address no other joint in this panel is using,
    // which numbers a robot's servos 1, 2, 3 down the list -- the same rule
    // Rust applies to a plan a saved file leaves unaddressed, so a panel that
    // is never touched agrees with a file that never mentions it.
    const setBusAddressing = () => {
      const ids = actuatorBusIdRange(actuator.value);
      busIdField.hidden = ids.length < 2;
      if (busIdField.hidden) {
        return;
      }
      const [low, high] = ids;
      busId.min = String(low);
      busId.max = String(high);
      busRange.textContent = `${low} to ${high}`;
      if (busId.value === "") {
        const used = rows
          .filter((other) => !other.busIdField.hidden)
          .map((other) => Number(other.busId.value));
        let free = low;
        while (used.includes(free) && free < high) {
          free += 1;
        }
        busId.value = String(free);
      }
    };

    // The speed a gearmotor was bought at. Only asked about for an actuator
    // Rust hands back speeds for, and with no placeholder: the plan fits one at
    // a default speed, which is a working answer the user is free to change.
    // It is the number on the listing rather than the gear ratio, because the
    // listing is what the user has to hand.
    const rpm = document.createElement("select");
    const rpmField = labelledField("Speed (rpm at 6 V)", rpm);
    rpmField.className = "joint-field";
    const setRpmOptions = () => {
      // An array of strings rather than the Float64Array wasm hands back, whose
      // own `map` would turn them straight back into numbers.
      const speeds = Array.from(actuatorRpmOptions(actuator.value), String);
      rpmField.hidden = speeds.length === 0;
      if (rpmField.hidden) {
        return;
      }
      const kept = rpm.value;
      rpm.replaceChildren(...speeds.map((speed) => new Option(speed, speed)));
      rpm.value = speeds.includes(kept)
        ? kept
        : speeds.includes(DEFAULT_MOTOR_RPM)
          ? DEFAULT_MOTOR_RPM
          : speeds[0];
    };

    // What the motor is wired through. Options and value both come from the
    // plan, through `refreshDrivers`, once every row exists.
    const driver = document.createElement("select");
    const driverField = labelledField("Driver", driver);
    driverField.className = "joint-field";

    // Whatever a saved configuration fitted to this joint, shown as it was
    // stored: the plan keeps the exact angle, this is only what is legible.
    const fitted = plan.actuatorName(j);
    const degrees = plan.servoDegreesAtZeroPose(j);
    const address = plan.busId(j);
    const speed = plan.motorRpm(j);
    if (plan.isFree(j)) {
      actuator.value = FREE_JOINT;
    } else if (fitted !== undefined) {
      actuator.value = fitted;
      setTravel();
      if (degrees !== undefined) {
        angle.value = degrees.toFixed(1);
      }
      // Whatever a saved configuration addressed this servo at, before the
      // default below would pick one for it.
      if (address !== undefined) {
        busId.value = String(address);
      }
      setRpmOptions();
      if (speed !== undefined) {
        rpm.value = String(speed);
      }
    }
    // No reading to ask for until there is a horn to read it off -- and none
    // on an actuator that has no horn, which Rust says by giving no travel.
    const noReading = () =>
      actuator.value === "" ||
      actuator.value === FREE_JOINT ||
      actuatorTravelDegrees(actuator.value).length < 2;
    angleField.hidden = noReading();
    setBusAddressing();
    setRpmOptions();

    actuator.addEventListener("change", () => {
      angleField.hidden = noReading();
      setTravel();
      setBusAddressing();
      setRpmOptions();
      // An actuator that needs no driver lets go of the channel this joint
      // held straight away, rather than when the step is left, so the other
      // motors' selects can offer it -- and a driver it was the last motor on
      // comes off the board.
      if (actuatorDriverOptions(actuator.value).length === 0) {
        plan.clearMotorDriver(j);
      }
      refreshDrivers();
      buildComponents();
      check();
    });
    angle.addEventListener("input", check);
    busId.addEventListener("input", check);
    driver.addEventListener("change", () => {
      try {
        if (driver.value === "") {
          plan.clearMotorDriver(j);
        } else if (driver.value.startsWith(NEW_DRIVER)) {
          plan.setNewMotorDriver(j, driver.value.slice(NEW_DRIVER.length));
        } else {
          const [d, c] = driver.value.split(":").map(Number);
          plan.setMotorDriver(j, d, c);
        }
      } catch (error) {
        showStatus(`Could not configure ${plan.name}: ${error}`);
      }
      refreshDrivers();
      buildComponents();
      check();
    });

    row.append(
      name,
      actuatorField,
      angleField,
      rpmField,
      driverField,
      busIdField,
    );
    jointList.append(row);
    rows.push({
      actuator,
      angle,
      angleField,
      rpm,
      rpmField,
      driver,
      driverField,
      busId,
      busIdField,
    });
  }
  refreshDrivers();

  if (rows.length === 0) {
    jointList.append(
      emptyNote("No rotary joints: nothing to fit an actuator to."),
    );
  }

  // The cameras the URDF draws, if it draws any. A robot with none gets no
  // section at all rather than an empty one: there is nothing the user could do
  // about it here -- a camera arrives by being drawn in the URDF, under the one
  // link name that is read as one.
  cameraList.replaceChildren();
  if (plan.cameraCount > 0) {
    cameraList.append(sectionLabel("Cameras"));
    const sensors = cameraOptions();
    for (let c = 0; c < plan.cameraCount; c++) {
      const row = document.createElement("div");
      row.className = "joint";

      const name = document.createElement("div");
      name.className = "joint-name";
      name.textContent = plan.cameraLink(c) ?? `camera ${c}`;

      const sensor = choiceSelect("Select a camera…", sensors);
      const sensorField = labelledField("Camera model", sensor);
      sensorField.className = "joint-field";
      // Whatever a saved configuration already fitted, as with an actuator.
      sensor.value = plan.cameraModel(c) ?? "";
      sensor.addEventListener("change", check);

      // What the picture will be of, which is a promise about the link's axes
      // rather than anything the user chooses here.
      const axis = document.createElement("div");
      axis.className = "joint-limit";
      axis.style.marginTop = "6px";
      axis.textContent = "Looks down the link's +Z, +Y down the image";

      row.append(name, sensorField, axis);
      cameraList.append(row);
      cameraRows.push(sensor);
    }
  }
  check();

  // The parts on the board rather than on the robot. Unlike a joint, which the
  // URDF decides there is exactly one of, these are added and removed by the
  // user, so the list is rebuilt from the plan each time it changes -- the plan
  // is what holds them, and removing one moves everything after it up a place.
  //
  // The motor drivers go first, in a section of their own: they are the user's
  // only indirectly, and the components keep the last place in the list so the
  // buttons that add them sit right under them.
  const buildComponents = () => {
    switchList.replaceChildren();

    // The motor drivers, which are on the board too but not added by the user:
    // a driver comes with the first motor given it and goes with the last, so
    // it has a name to edit and no remove button. Under the name, which motor
    // is on each channel -- which is also how full it is. No section at all
    // without one, as with a camera: one arrives by picking it for a motor.
    if (plan.driverCount > 0) {
      switchList.append(sectionLabel("Motor drivers"));
    }
    for (let d = 0; d < plan.driverCount; d++) {
      const row = document.createElement("div");
      row.className = "joint";

      const name = document.createElement("input");
      name.type = "text";
      name.value = plan.driverName(d) ?? "";
      // The motors' driver selects are labelled with it, so they follow.
      name.addEventListener("input", () => {
        plan.setDriverName(d, name.value);
        refreshDrivers();
      });
      const head = document.createElement("div");
      head.className = "component-head";
      head.append(name);

      const kind = document.createElement("div");
      kind.className = "joint-actuator";
      kind.textContent = plan.driverKind(d) ?? "";

      const channels = document.createElement("div");
      channels.className = "joint-limit";
      const used: string[] = [];
      for (let c = 0; c < plan.driverChannelCount(d); c++) {
        const holder = plan.driverChannelJoint(d, c);
        const motor =
          holder === undefined
            ? "free"
            : (plan.jointName(holder) ?? `joint ${holder}`);
        used.push(`${plan.driverChannelName(d, c)}: ${motor}`);
      }
      channels.textContent = `Channels ${used.join(" · ")}`;

      row.append(head, kind, channels);
      switchList.append(row);
    }

    switchList.append(sectionLabel("Components"));
    for (let c = 0; c < plan.componentCount; c++) {
      const row = document.createElement("div");
      row.className = "joint";

      // The name is the user's: it is what the button pressing this switch is
      // labelled, and what a warning about it says. Written straight into the
      // plan, since nothing else depends on it.
      const name = document.createElement("input");
      name.type = "text";
      name.value = plan.componentName(c) ?? "";
      name.addEventListener("input", () =>
        plan.setComponentName(c, name.value),
      );

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "component-remove";
      remove.title = "Remove";
      remove.textContent = "×";
      remove.addEventListener("click", () => {
        plan.removeComponent(c);
        buildComponents();
      });

      const head = document.createElement("div");
      head.className = "component-head";
      head.append(name, remove);

      const kind = document.createElement("div");
      kind.className = "joint-actuator";
      kind.textContent = plan.componentKind(c) ?? "";

      row.append(head, kind);
      switchList.append(row);
    }

    if (plan.componentCount === 0) {
      switchList.append(emptyNote("None. A robot does not need any."));
    }
  };

  // One button per kind on offer -- a tactile switch, a thumb joystick. Built
  // from the same table the plan validates against, so a further kind is a
  // change in Rust and nothing here.
  componentActions.replaceChildren(
    ...componentOptions().map((kind) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = `Add ${kind.toLowerCase()}`;
      button.addEventListener("click", () => {
        try {
          plan.addComponent(kind);
        } catch (error) {
          showStatus(`Could not add ${kind}: ${error}`);
          return;
        }
        buildComponents();
      });
      return button;
    }),
  );
  buildComponents();

  // Leaving the hardware step writes it into the plan, which is what the wiring
  // step then reads: the wires belong to the actuator on each joint, and the
  // pins on offer are the chosen board's. Rust wires each actuator up as it is
  // fitted, so the selects below start on a working default rather than blank.
  //
  // Fitting an actuator starts its wiring over from the defaults, so a joint is
  // only written back when the user has actually changed it: coming back from
  // the wiring step to adjust one joint leaves the pins picked for the others
  // alone.
  // Seeded from what a saved configuration already fitted, for the same reason:
  // re-fitting those joints would start their wiring over from the defaults and
  // throw away the pins the file named.
  //
  // A free joint is keyed by the bare sentinel, since it has no angle to change.
  // Neither has a gearmotor, whose box is hidden and may still hold whatever a
  // servo chosen earlier left in it -- so that is read as the 0 it stands for.
  const angleOf = (row: JointRow): number =>
    row.angleField.hidden ? 0 : Number(row.angle.value);
  const chosenFor = (row: JointRow): string =>
    row.actuator.value === FREE_JOINT
      ? FREE_JOINT
      : `${row.actuator.value} at ${angleOf(row)}`;
  const applied: string[] = rows.map((row, j) =>
    plan.isFree(j) || plan.actuatorName(j) !== undefined ? chosenFor(row) : "",
  );
  const applyHardware = (): boolean => {
    try {
      plan.setMicrocontroller(board.value);
      // After the board, which starts it over at its default.
      if (!psramField.hidden) {
        plan.setPsram(psram.value);
      }
      plan.setPower(power.value);
      plan.setStepDown(stepDown.value);
      rows.forEach((row, j) => {
        const chosen = chosenFor(row);
        if (chosen !== applied[j]) {
          if (chosen === FREE_JOINT) {
            plan.setFree(j);
          } else {
            plan.setActuator(j, row.actuator.value, angleOf(row));
          }
          applied[j] = chosen;
        }
        // Applied every pass rather than only when changed, like a camera and
        // for the same reason: re-addressing a servo starts nothing over, since
        // an address is not a pin and costs the joint no wiring. Fitting the
        // actuator just picked a default one, which this is what overrides.
        if (!row.busIdField.hidden) {
          plan.setBusId(j, Number(row.busId.value));
        }
        // The same for a gearmotor's speed: another gearbox is the same wires
        // in the same pins, so it is applied every pass and starts nothing over.
        if (!row.rpmField.hidden) {
          plan.setMotorRpm(j, Number(row.rpm.value));
        }
      });
      // Applied every pass rather than only when changed: fitting a sensor
      // starts nothing over, since a camera has no wiring to lose.
      cameraRows.forEach((sensor, c) => plan.setCamera(c, sensor.value));
    } catch (error) {
      showStatus(`Could not configure ${plan.name}: ${error}`);
      return false;
    }
    return true;
  };

  // The rules live in Rust; here they are one message under the wiring, and no
  // adding until it goes away -- and, beside it, one that is only a caution:
  // wiring that runs here and would hurt a real board, which moves with the
  // pins as a problem does but leaves "Add" alone.
  const checkWiring = () => {
    const found = plan.wiringProblem();
    problem.textContent = found ?? "";
    problem.hidden = found === undefined;
    add.disabled = found !== undefined;
    const careful = plan.wiringCaution();
    caution.textContent = careful ?? "";
    caution.hidden = careful === undefined;
  };

  // One wire's pin, as a select over the pins that carry what it needs. Both
  // lists below build the same control; only where the answer is written
  // differs, which is what `connect` carries.
  const pinField = (
    label: string,
    options: string[],
    chosen: string | undefined,
    connect: (pin: string) => void,
  ): HTMLLabelElement => {
    // No placeholder option: a wire arrives already on a pin, and the only
    // choices offered are pins that carry what it needs.
    const pin = document.createElement("select");
    pin.append(...options.map((name) => new Option(name, name)));
    if (chosen !== undefined) {
      pin.value = chosen;
    }
    pin.addEventListener("change", () => {
      try {
        connect(pin.value);
      } catch (error) {
        showStatus(`Could not wire ${plan.name}: ${error}`);
      }
      checkWiring();
    });

    const field = labelledField(label, pin);
    field.className = "joint-field";
    return field;
  };

  const buildWiring = () => {
    wiringBoard.textContent = `Pins on ${board.value}`;

    // The battery and the converter, whose wiring to each other is fixed --
    // the converter's input has nothing else to be on -- so they are shown
    // rather than asked about. What they put out are the two rails the power
    // selects below offer beside the board's own pins.
    const powerPart = (title: string, kind: string, lines: string[]) => {
      const row = document.createElement("div");
      row.className = "joint";
      const name = document.createElement("div");
      name.className = "joint-name";
      name.textContent = title;
      const what = document.createElement("div");
      what.className = "joint-actuator";
      what.textContent = kind;
      row.append(name, what);
      for (const line of lines) {
        const shown = document.createElement("div");
        shown.className = "joint-limit";
        shown.textContent = line;
        row.append(shown);
      }
      return row;
    };
    wiringPower.replaceChildren(
      sectionLabel("Power"),
      powerPart(plan.power, "Power source", [
        `+ and − → ${plan.stepDown} IN+ and IN−`,
        `Puts out the ${plan.powerRail} rail`,
      ]),
      powerPart(plan.stepDown, "Step-down converter", [
        `IN from ${plan.power}`,
        `Puts out the ${plan.stepDownRail} rail`,
      ]),
      emptyNote(
        "A part's power wire can go on either rail as well as the board's " +
          "own, and is only offered the ones it is rated for. Every ground " +
          "is common.",
      ),
    );

    wiringJoints.replaceChildren(sectionLabel("Joints"));
    wiringSwitches.replaceChildren();

    for (let j = 0; j < plan.jointCount; j++) {
      const wires = plan.wireCount(j);
      if (wires === 0) {
        continue;
      }

      const row = document.createElement("div");
      row.className = "joint";
      const name = document.createElement("div");
      name.className = "joint-name";
      name.textContent = plan.jointName(j) ?? `joint ${j}`;
      const actuator = document.createElement("div");
      actuator.className = "joint-actuator";
      actuator.textContent = rows[j].actuator.value;
      row.append(name, actuator);

      // A motor's inputs are its driver's wires, listed under the driver
      // below; here, only which one and where on it.
      const d = plan.motorDriver(j);
      const c = plan.motorChannel(j);
      if (d !== undefined && c !== undefined) {
        const through = document.createElement("div");
        through.className = "joint-limit";
        through.textContent =
          `Through ${plan.driverName(d)}, channel ` +
          `${plan.driverChannelName(d, c)}`;
        row.append(through);
      }

      for (let w = 0; w < wires; w++) {
        row.append(
          pinField(
            plan.wireName(j, w) ?? `Wire ${w}`,
            plan.pinOptions(j, w),
            plan.wirePin(j, w),
            (pin) => plan.setWire(j, w, pin),
          ),
        );
      }

      wiringJoints.append(row);
    }

    // Only the section label, so nothing was wired.
    if (wiringJoints.childElementCount === 1) {
      wiringJoints.append(emptyNote("No actuators: nothing to wire up."));
    }

    // What a per-wire select cannot say about a bus: the pin is the harness
    // rather than something the emulated board reads, and the servos want a
    // supply the board's rails are not. Shown only when there is a bus, since
    // otherwise it is about nothing on this robot.
    if (rows.some((row) => !row.busIdField.hidden)) {
      wiringJoints.append(
        emptyNote(
          "Bus servos share one line, which the board talks over as Serial1 — " +
            "Serial1.begin(1000000, SERIAL_8N1, rx, tx). The emulated UART has " +
            "no pin mux, so they hear it whichever pins the sketch names. They " +
            "want 6–8.4 V, which is the battery's rail and none of the board's.",
        ),
      );
    }

    // The same for a gearmotor: what the pins mean, and the one thing about
    // driving it that differs between the boards. Shown only when one is fitted.
    if (rows.some((row) => !row.rpmField.hidden)) {
      const speed =
        board.value === ARDUINO_NANO
          ? "On a Nano, analogWrite only reaches the pin at 0 and 255 — the " +
            "emulated timers do not drive their PWM pins — so set the speed " +
            "with a software PWM, or run it full on and off."
          : "On an ESP32-S3, analogWrite takes a PWM channel per pin it is " +
            "called on, so a motor driven both ways uses two of the eight.";
      wiringJoints.append(
        emptyNote(
          "An N20's own leads go to its driver's outputs, never to the " +
            "board, so what is wired here is its encoder: it drives A and B " +
            "back — read them with attachInterrupt(pin, isr, CHANGE) — " +
            "swinging to whichever rail its VCC is on. Its inputs are its " +
            "driver channel's, wired under the driver below, and its speed " +
            "scales with the rail that driver's VM is on: it was sold at " +
            `6 V. ${speed}`,
        ),
      );
    }

    // More PWM than the board has channels for. Not a wiring problem, since a
    // sketch that drives the rest with a software PWM runs fine, so it is said
    // here and "Add" stays enabled. It depends only on the actuators, which
    // were settled in the hardware step, so no pin change below can alter it.
    const note = plan.wiringNote();
    if (note !== undefined) {
      wiringJoints.append(emptyNote(note));
    }

    // The components read the board rather than being driven by it, but as far
    // as the wiring goes they are the same thing: legs in pins, out of the same
    // pool the servos draw from. A joystick's two wipers are the one wire here
    // that is fussy about which pin it lands in -- it has to be one a converter
    // reaches -- and `componentPinOptions` is what says so.
    //
    // The drivers first, in a section of their own, since the motors above
    // point at them: the shared supply and nSLEEP, then two inputs for each
    // channel a motor is on.
    if (plan.driverCount > 0) {
      wiringSwitches.append(sectionLabel("Motor drivers"));
    }
    for (let d = 0; d < plan.driverCount; d++) {
      const row = document.createElement("div");
      row.className = "joint";
      const name = document.createElement("div");
      name.className = "joint-name";
      name.textContent = plan.driverName(d) ?? `driver ${d}`;
      const kind = document.createElement("div");
      kind.className = "joint-actuator";
      kind.textContent = plan.driverKind(d) ?? "";
      row.append(name, kind);

      for (let w = 0; w < plan.driverWireCount(d); w++) {
        row.append(
          pinField(
            plan.driverWireName(d, w) ?? `Wire ${w}`,
            plan.driverPinOptions(d, w),
            plan.driverWirePin(d, w),
            (pin) => plan.setDriverWire(d, w, pin),
          ),
        );
      }

      const note = plan.driverNote(d);
      if (note !== undefined) {
        row.append(emptyNote(note));
      }
      wiringSwitches.append(row);
    }
    if (plan.componentCount > 0) {
      wiringSwitches.append(sectionLabel("Components"));
    }
    for (let c = 0; c < plan.componentCount; c++) {
      const row = document.createElement("div");
      row.className = "joint";
      const name = document.createElement("div");
      name.className = "joint-name";
      name.textContent = plan.componentName(c) ?? `component ${c}`;
      const kind = document.createElement("div");
      kind.className = "joint-actuator";
      kind.textContent = plan.componentKind(c) ?? "";
      row.append(name, kind);

      for (let w = 0; w < plan.componentWireCount(c); w++) {
        row.append(
          pinField(
            plan.componentWireName(c, w) ?? `Wire ${w}`,
            plan.componentPinOptions(c, w),
            plan.componentWirePin(c, w),
            (pin) => plan.setComponentWire(c, w, pin),
          ),
        );
      }

      // What the selects above cannot say about this kind of part -- which
      // pins its wires are offered and why, and what it wants of the rail it
      // is on. It comes off the same table the part itself does, so a kind
      // that needs explaining explains itself.
      const note = plan.componentNote(c);
      if (note !== undefined) {
        row.append(emptyNote(note));
      }

      wiringSwitches.append(row);
    }

    checkWiring();
  };

  const showWiring = (showing: boolean) => {
    hardware.hidden = showing;
    wiring.hidden = !showing;
    next.hidden = showing;
    back.hidden = !showing;
    add.hidden = !showing;
    if (!showing) {
      problem.hidden = true;
      caution.hidden = true;
    }
  };

  return new Promise((resolve) => {
    const close = (committed: boolean) => {
      panel.hidden = true;
      note.hidden = true;
      problem.hidden = true;
      caution.hidden = true;
      next.removeEventListener("click", onNext);
      back.removeEventListener("click", onBack);
      add.removeEventListener("click", onAdd);
      cancel.removeEventListener("click", onCancel);
      resolve(committed);
    };
    // The hardware is re-applied on every pass forward, so a joint edited after
    // a trip back through "Back" is the one that gets wired.
    const onNext = () => {
      if (!applyHardware()) {
        return;
      }
      buildWiring();
      showWiring(true);
    };
    const onBack = () => showWiring(false);
    // Nothing left to collect: both steps wrote into the plan as they were left.
    const onAdd = () => close(true);
    const onCancel = () => close(false);

    next.addEventListener("click", onNext);
    back.addEventListener("click", onBack);
    add.addEventListener("click", onAdd);
    cancel.addEventListener("click", onCancel);
    showWiring(false);
    panel.hidden = false;
  });
}

function initSimulator() {
  let interfaceSimulator = null;
  let showGrid = true;
  let simulator = new Simulator(interfaceSimulator, showGrid);

  createEnv().then((hybrid) => {
    simulator.addHybrid(hybrid);
    simulator.updateHybrid();

    const cameraPosition = {
      eye: { x: 0.0, y: -0.5, z: 0.1 },
      target: { x: 0.0, y: 0, z: 0 },
    };
    simulator.graphics.lookAt(cameraPosition);
    simulator.run(30, 0);
    showRealtimeRate(simulator, hybrid);
    // The scene is the same one for the life of the page -- a drop replaces
    // what is in it, never the handle -- so the modes are armed once here and
    // work out which body they are pushing or welding at each pick. Both answer
    // a press on the canvas, so each switches the other off as it arms; that is
    // wired here rather than by the two importing each other.
    allowNudging(simulator, hybrid, disarmFixing);
    allowFixing(simulator, hybrid, disarmNudging);

    // Rust owns the drop end to end: it walks the dropped directory, reads every
    // file into wasm, and parses the URDF it finds. Nothing is built, and
    // nothing joins the running Hybrid, until the panel below has been told what
    // hardware drives the robot.
    let configuring = false;
    let placing = false;
    // The objects placed in the scene so far. Unlike the robot there can be any
    // number of them and they are never taken out again, so this only grows --
    // it is here to be handed to `keepOnlyRobot`, which would otherwise clear
    // them along with the robot they were dropped beside.
    const objects: AddedObject[] = [];
    // The plan the robot in the scene was built from. Building borrows the plan
    // rather than spending it, and "Edit config" answers the panel again and
    // builds the same plan a second time -- so it is kept for as long as the
    // robot it describes is in the scene. This is the only handle to that wasm
    // memory, the dropped folder the meshes are re-read from included, so it is
    // freed when the next robot replaces the one it built.
    let built: RobotPlan | undefined;
    registerFolderDrop(
      "threejs",
      onFolder,
      // Reading the drop is a file at a time with an `await` between each, so
      // the browser interleaves the page's animation frames with it -- and a
      // frame of a scene that already has a robot in it is mostly an emulated
      // microcontroller. Left running, the second drop of a folder takes many
      // times what the first one did, for no reason the user can see. Nothing
      // is being watched while a robot is being read, so the physics is simply
      // stopped for the duration; the view still renders and still orbits.
      //
      // It is also the only part of a drop with nothing to show for itself: the
      // folder has no name here yet, since the entry the name comes off is one
      // of the things being read.
      //
      // Placing a dropped object is a second, longer freeze that this must not
      // undo: a drop that arrives mid-placement is refused a moment later, and
      // between the end of its read and that refusal the physics would
      // otherwise start again underneath the panel.
      (reading: boolean) => {
        simulator.paused = reading || placing;
        if (placing) {
          return;
        }
        showStatus(reading ? "Reading the dropped folder..." : DROP_PROMPT);
      },
    );

    function onFolder(folder: DroppedFolder) {
      if (configuring || placing) {
        showStatus("Finish with the folder already dropped first.");
        folder.free();
        return;
      }

      // A folder with no URDF in it is not a robot. If it holds a mesh it is an
      // object: one shape, no joints, and no hardware to fit -- the only things
      // it leaves open are what it is made of and where it goes, and both are
      // answered with it already in the scene.
      if (shallowestFile(folder, ".urdf") === undefined) {
        onObjectFolder(folder);
        return;
      }

      // planRobotFromFolder takes the folder -- the plan keeps it until the
      // meshes are read at build time -- so this handle is spent either way,
      // and its name (and anything else read out of it) has to be taken while
      // it still has one.
      const folderName = folder.name;
      const saved = readSavedConfig(folder);
      // Read here for the same reason the configuration is: the folder is spent
      // by the call below, and the editor is not opened until the panel has been
      // answered -- which may be a while, or never.
      const sketches = readDroppedSketches(folder);
      let plan: RobotPlan | undefined;
      try {
        plan = planRobotFromFolder(folder);
      } catch (error) {
        showStatus(`Could not load ${folderName}: ${error}`);
      }
      if (!plan) {
        return;
      }

      // A const so the callback below keeps the narrowing.
      const robot = plan;
      const name = robot.name;

      // A configuration saved from an earlier run answers the panel's questions.
      // It is applied for whatever it is worth; only a file that leaves nothing
      // over skips the panel, and anything else opens it prefilled with what did
      // apply, so the user fixes the difference rather than starting again.
      const notes = saved === undefined ? [] : applyConfig(robot, saved);
      for (const note of notes) {
        console.warn(`${CONFIG_FILE}: ${note}`);
      }
      const ready = saved !== undefined && notes.length === 0;

      if (ready) {
        showStatus(`${name}: hardware read from ${CONFIG_FILE}.`);
      } else {
        configuring = true;
        showStatus(
          `${name}: ${robot.jointCount} joint(s) to configure. ` +
            `Details in the console.`,
        );
      }

      const answered = ready
        ? Promise.resolve(true)
        : configureRobot(robot, notes);
      answered.then((committed) => {
        configuring = false;
        if (!committed) {
          robot.free();
          // Nothing was built, so the notes are about a robot that does not
          // exist. They stay in the console; the panel is for what is in the
          // scene.
          clearWarnings();
          showStatus(DROP_PROMPT);
          return;
        }
        // A build that fails is the one place a dropped plan is freed by hand:
        // the robot already in the scene, and the plan it was built from, carry
        // on without it.
        const rotations =
          saved === undefined ? new Map() : readViewRotations(saved);
        if (!buildRobot(robot, sketches, rotations)) {
          robot.free();
        }
      });
    }

    // Everything a configured plan needs to become the robot on screen. It is
    // the tail of a drop and the whole of an "Edit config" commit -- the two
    // differ only in where the plan came from and whether the drop brought
    // sketches with it, so they share this rather than each having a copy.
    //
    // `rotations` is how far each camera view is turned, by link: from the
    // saved file on a drop, and from the panel as it stands on a rebuild.
    function buildRobot(
      plan: RobotPlan,
      sketches: DroppedSketch[],
      rotations: Map<string, number>,
    ): boolean {
      const name = plan.name;
      try {
        // The answers as a file, which is what the editor's "Save config" hands
        // to the browser. Written on every build, so a robot rebuilt from an
        // edited plan saves what it was actually rebuilt from.
        const config = configFromPlan(plan);
        // Shown *before* the build rather than after it, now that the
        // configuration panel has closed and freed the corner. A URDF broken
        // badly enough to bring `addTo` down takes wasm with it, and the note
        // that explains why would never be shown if this waited for it.
        showWarnings(name, plan.notes);
        const added = plan.addTo(hybrid);
        // The other half, which is only knowable once the meshes have been
        // read: what building the robot skipped or approximated.
        addWarnings(added.notes);
        // A new robot replaces the one in the scene rather than joining it,
        // whether it was dropped or is this one rebuilt from edited answers.
        // Not before the build: a robot that fails to build leaves the one
        // already running alone, and a configuration the user backs out of
        // never gets here at all. `added` comes back renumbered to index 0.
        keepOnlyRobot(simulator, hybrid, added, objects);
        addArticulatedVisuals(simulator, hybrid, added.index);
        // `addTo` also switches the robot's microcontroller on. The editor is
        // what the user talks to it with from here: it opens on the firmware
        // the board booted with, and its serial monitor on what that prints.
        // It keeps the handle, which is what puts the robot back in its
        // starting pose when a new sketch is run, and the configuration, which
        // is what its "Save config" downloads. Sketches that came with the
        // folder open in place of that firmware, the first of them showing
        // and the rest a pick away -- shown, not flashed; the board is
        // running what it booted with until Run is pressed.
        editFirmwareOf(hybrid, added, name, config, sketches, () => {
          void editConfigOf(plan);
        });
        // The switches on that board, as buttons to hold down. It reads them
        // off the handle rather than the plan: the handle is what the panels
        // are given, and it is renumbered to match the scene.
        pressSwitchesOf(hybrid, added);
        // And its joysticks, as pads to drag, in the panel under them. Both
        // read the same handle for the same reason; the wiring warning they
        // share is polled by the switches, whichever of the two the fault is
        // on.
        useJoysticksOf(hybrid, added);
        // And what its cameras see, drawn from the same scene the visuals
        // above were just added to -- which is why this comes after them:
        // the view hides the camera's own link, and that link's meshes do
        // not exist until `addArticulatedVisuals` has made them.
        showCameraViewOf(simulator, hybrid, added, rotations);
        // And its radio, if it has one. Nothing happens here until a sketch
        // opens a socket: this only carries what the emulator cannot do for
        // itself, which is the connection to the outside world.
        connectNetworkOf(hybrid, added);
        // The robot that was in the scene has just gone, and so has any use for
        // the plan it was built from -- unless this build *is* that plan being
        // built again, which is what "Edit config" does.
        if (built !== plan) {
          built?.free();
          built = plan;
        }
        showStatus(
          `Added ${name}: ${hybrid.n_bodies_articulated(added.index)} bodies, ` +
            `microcontroller running.`,
        );
        return true;
      } catch (error) {
        showStatus(`Could not add ${name}: ${error}`);
        return false;
      }
    }

    // "Edit config": the configuration panel again, on the plan the robot in
    // the scene was built from, and a rebuild from the answers if the user
    // commits.
    //
    // Rebuilding is not a choice, it is the same rule that made the panel come
    // before the build in the first place: a joint carries its actuator's
    // armature and dry friction, and both go on at construction, so there is no
    // changing the hardware of a robot that already exists. The edit
    // necessarily discards it -- the robot comes back in its build pose with its
    // board rebooted on the firmware it shipped with, and the button's title
    // says so before it is pressed.
    async function editConfigOf(plan: RobotPlan) {
      // The configuration panel is one panel in one corner, and a dropped
      // object has the physics stopped around it.
      if (configuring || placing) {
        showStatus("Finish with the panel already open first.");
        return;
      }
      // What the robot was actually built from, read before the panel can move
      // the plan: the hardware step is applied as it is left, so a user who
      // presses "Next" and then "Cancel" has already changed it.
      const before = configFromPlan(plan);
      configuring = true;
      showStatus(
        `Editing ${plan.name}: committing rebuilds it in its build pose.`,
      );
      // No notes: this panel is not opening because a saved file left something
      // unanswered, it is opening because the user asked for it.
      const committed = await configureRobot(plan, []);
      configuring = false;
      if (!committed) {
        restoreConfig(plan, before);
        showStatus(DROP_PROMPT);
        return;
      }
      // The camera views stay turned the way the user left them: the turn is
      // the panel's, and nothing the configuration panel asks changes it.
      buildRobot(plan, [], cameraViewRotations());
    }

    // A dropped mesh, which is the other thing a folder can be.
    //
    // The order is the opposite of the robot's and deliberately so: the object
    // is built into the scene first and asked about afterwards. Nothing about it
    // has to be known at construction -- density only scales an inertia that is
    // already integrated, and where it goes is a joint position -- so building
    // it first is what lets the user answer both questions while looking at the
    // thing, with the physics stopped around them.
    function onObjectFolder(folder: DroppedFolder) {
      const folderName = folder.name;
      // The read has already ended and unpaused the scene (`onReading` above);
      // placing is a second, longer freeze, and it lasts until the user is done.
      simulator.paused = true;

      let added: AddedObject | undefined;
      try {
        added = addObjectFromFolder(folder, hybrid);
      } catch (error) {
        simulator.paused = false;
        showStatus(`Could not load ${folderName}: ${error}`);
        return;
      }

      const object = added;
      placing = true;
      // The placement gizmo takes the canvas and the physics stays stopped for
      // it, so the modes that work the canvas are switched off for the duration
      // rather than left on and quietly doing nothing.
      setNudgingAllowed(false);
      setFixingAllowed(false);
      addArticulatedVisuals(simulator, hybrid, object.index);
      // The mesh has just been made and is still sitting at the origin: poses
      // are pushed into meshes by `updateHybrid`, which only runs on a frame
      // that stepped, and nothing is stepping. One call puts every mesh where
      // its body is -- which for everything but the new object is where it
      // already was.
      simulator.updateHybrid();
      showStatus(`Place ${object.name}: drag it into position.`);

      placeObject(simulator, hybrid, object).then((placed) => {
        placing = false;
        simulator.paused = false;
        setNudgingAllowed(true);
        setFixingAllowed(true);
        if (!placed) {
          // Visuals first, while the body is still there to name them -- the
          // same order `keepOnlyRobot` takes for the same reason.
          removeArticulatedVisuals(simulator, hybrid, object.index);
          object.removeFrom(hybrid);
          object.free();
          // Nothing was shown for it, so there is nothing to take down --
          // and whatever the robot in the scene had to say still stands.
          showStatus(DROP_PROMPT);
          return;
        }
        // What reading the mesh had to approximate: what its `.mtl` could not
        // answer for, whether its volume could be trusted, and what it will and
        // will not collide with. Shown now rather than before the panel, since
        // the two share the top-right corner -- and unlike a robot's, these
        // notes are all in hand before anything can go wrong.
        showWarnings(object.name, object.notes);
        // Kept for the life of the page: the handle is what says which of the
        // scene's articulateds are objects when the next robot replaces the
        // one in it.
        objects.push(object);
        showStatus(
          `Placed ${object.name}: ${(1000 * object.mass).toFixed(1)} g. ` +
            DROP_PROMPT,
        );
      });
    }
  });
}

initSimulator();
